import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  LocationOn as LocationIcon,
  Business as SiteIcon,
  Home as ApartmentIcon,
  Villa as VillaIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  EventAvailable as EventAvailableIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';

import { getProjects, searchInventory } from '../../services/project';
import { exportAsExcel, exportAsCSV, exportAsPDF } from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const INITIAL_INVENTORY = [

];

const normalizeInventoryData = (items = [], commissionData = []) => {
  const toNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  return items
    .filter(Boolean)
    .map((item, index) => {
      const inventoryType = item.inventoryType || item.projectType || item.type || 'Plot';
      const address = item.address || item.location || {};
      const projectInfo = item.project || {};
      const derivedName =
        inventoryType.toLowerCase() === 'apartment'
          ? item.buildingName || item.unitName || item.projectName || item.project || item.plotName
          : item.plotName || item.unitName || item.projectName || item.project;

      const projectName =
        item?.projectData?.projectName ||
        projectInfo.projectName ||
        item.projectName ||
        item.project ||
        derivedName ||
        'Unnamed Project';

      const identifier =
        item.inventoryCode ||
        item.plotNumber ||
        item.unitNumber ||
        item.plotId ||
        item.plotName ||
        item.buildingName ||
        item.identifier ||
        `INV-${index + 1}`;

      const width = item.width ?? item.w1;
      const length = item.length ?? item.l1;
      const sizeString =
        item.size ||
        item.plotSize ||
        item.unitSize ||
        (width && length ? `${width} x ${length}` : '');

      return {
        id: item._id || item.id || item.inventoryId || `INV-${index + 1}`,
        type: inventoryType,
        identifier,
        name: derivedName || projectName,
        projectName,
        siteName: item.siteName || projectName,
        sector: item.sector || item.section || '',
        block: item.block || '',
        plotAmount: toNumber(item.amount ?? item.plotAmount ?? item.price),
        bookingAmount: toNumber(item.bookingAmount),
        pendingAmount: toNumber(item.pendingAmount),
        pricePerSqFt: toNumber(item.pricePerSqFt ?? item.unitValue ?? item.price),
        plcPercentage: toNumber(item.plcPercent ?? item.plcPercentage ?? item.plc),
        size: sizeString,
        status: item.status || 'No Status',
        totalUnits: toNumber(item.totalUnits ?? item.totalFlats),
        availableUnits: toNumber(item.availableUnits ?? item.unitsAvailable),
        bookedUnits: toNumber(item.bookedUnits ?? item.totalFlatsBooked),
        soldUnits: toNumber(item.soldUnits ?? item.totalFlatsSold),
        holdUnits: toNumber(item.holdUnits ?? item.totalFlatsHold),
        inventoryValue: toNumber(
          item.inventoryValue ?? item.totalInventoryValue ?? item.amount ?? 0
        ),
        city: address.city || item.city || '',
        state: address.state || item.state || '',
        address:
          address.fullAddress ||
          address.addressLine ||
          item.fullAddress ||
          item.address ||
          '',
        configuration: item.configuration || item.unitType || '',
        carpetArea: item.carpetArea || item.area || '',
        floor: item.floorNo || item.floor || item.floorRange || '',
        parking: item.parking || '',
        reraNo: item.reraNo || item.reraNumber || '',
        plotArea: item.plotArea || '',
        builtUpArea: item.builtUpArea || '',
        totalSize: toNumber(item.totalSize, ''),
        w1: toNumber(width, ''),
        l1: toNumber(length, ''),
        guidelineValue: toNumber(item.guidelineValue ?? item.unitValue ?? item.price),
        commission: item.commission || (commissionData.length > 0 ? `${commissionData[0].commissionPercentage}%` : ''),
        facing: item.facing || '',
        possessionDate: item.possessionDate || '',
        remark: item.remark || '',
        projectId: item.projectId || projectInfo._id || '',
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      };
    });
};

const Inventory = () => {
  // Unified inventory data with type field
  const [inventoryData, setInventoryData] = useState(INITIAL_INVENTORY);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceFrom, setPriceFrom] = useState('');
  const [priceTo, setPriceTo] = useState('');
  const [selectedProject, setSelectedProject] = useState('all'); // stores projectId or 'all'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState('');
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [summaryTotals, setSummaryTotals] = useState({ totalAmount: 0, count: 0, availableCount: 0, bookedCount: 0 });
  const [commissionData, setCommissionData] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loadingProjectTypes, setLoadingProjectTypes] = useState(false);

  // More Filters States
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [tempPriceFrom, setTempPriceFrom] = useState('');
  const [tempPriceTo, setTempPriceTo] = useState('');

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setPriceFrom(tempPriceFrom);
    setPriceTo(tempPriceTo);
    setPage(0);
    setMoreFiltersOpen(false);
    fetchInventory(0, rowsPerPage, {
      priceFrom: tempPriceFrom,
      priceTo: tempPriceTo,
      sortBy: tempSortBy,
    });
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setPriceFrom('');
    setPriceTo('');
    setTempPriceFrom('');
    setTempPriceTo('');
    setPage(0);
    fetchInventory(0, rowsPerPage, {
      priceFrom: '',
      priceTo: '',
      sortBy: '',
    });
  };


  const fetchInventory = async (
    requestedPage = page,
    requestedRows = rowsPerPage,
    overrides = {},
  ) => {
    setLoadingInventory(true);
    setFetchError('');
    try {
      const apiPage = requestedPage + 1;
      const {
        searchQuery: oq,
        searchType: ot,
        statusFilter: os,
        selectedProject: op,
        priceFrom: opf,
        priceTo: opt,
        sortBy: osb,
      } = overrides;

      const effectiveSearchQuery = oq !== undefined ? oq : searchQuery;
      const effectiveSearchType = ot !== undefined ? ot : searchType;
      const effectiveStatusFilter = os !== undefined ? os : statusFilter;
      const effectiveSelectedProject = op !== undefined ? op : selectedProject;
      const effectivePriceFrom = opf !== undefined ? opf : priceFrom;
      const effectivePriceTo = opt !== undefined ? opt : priceTo;
      const effectiveSortBy = osb !== undefined ? osb : sortBy;

      const params = {
        projectId:
          effectiveSelectedProject !== 'all' ? effectiveSelectedProject : undefined,
        search: effectiveSearchQuery || undefined,
        inventoryType:
          effectiveSearchType !== 'all' ? effectiveSearchType : undefined,
        status:
          effectiveStatusFilter !== 'all' ? effectiveStatusFilter : undefined,
        priceFrom: effectivePriceFrom || undefined,
        priceTo: effectivePriceTo || undefined,
        page: apiPage,
        limit: requestedRows,
      };

      if (effectiveSortBy) {
        params.sortBy = 'amount';
        params.sortOrder = effectiveSortBy === 'amountAsc' ? 'asc' : 'desc';
      }

      const body = await searchInventory(params);
      const payload = body?.data || {};
      const list = Array.isArray(payload?.inventories)
        ? payload.inventories
        : Array.isArray(body?.data)
          ? body.data
          : [];
      const normalized = normalizeInventoryData(list, commissionData);
      setInventoryData(normalized);
      setPage(requestedPage);
      setRowsPerPage(requestedRows);
      setTotalItems(
        payload?.pagination?.totalItems ??
        payload?.pagination?.totalItems ??
        list.length,
      );
      setSummaryTotals({
        totalAmount: Number(payload?.totalAmount) || 0,
        count: Number(payload?.pagination?.totalItems || payload?.count) || 0,
        availableCount: Number(payload?.availableCount) || 0,
        bookedCount: Number(payload?.bookedCount) || 0,
      });
      setSuccessMessage('');
    } catch (error) {
      setFetchError(error.message || 'Failed to load inventory.');
    } finally {
      setLoadingInventory(false);
    }
  };

  useEffect(() => {
    fetchInventory(0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load project list for Project dropdown based on selected type
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingProjects(true);
        const options = searchType !== 'all' ? { projectType: searchType } : {};
        const data = await getProjects(options);
        if (active) setProjects(Array.isArray(data) ? data : []);
      } catch {
        if (active) setProjects([]);
      } finally {
        if (active) setLoadingProjects(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [searchType]);

  // Fetch commissions
  const fetchCommissions = async () => {
    setLoadingCommissions(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=commission`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const commissions = Array.isArray(result.data)
        ? result.data
          .filter(item => item.status === true) // Only active commissions
          .map((item) => ({
            id: item._id,
            commissionPercentage: item.details?.commissionPercentage || 0,
            status: item.status === true ? 'Active' : 'Inactive',
            rawStatus: item.status,
          }))
          .sort((a, b) => b.commissionPercentage - a.commissionPercentage) // Sort by percentage descending
        : [];

      setCommissionData(commissions);
    } catch (err) {
      console.error('Failed to load commissions:', err);
      setCommissionData([]);
    } finally {
      setLoadingCommissions(false);
    }
  };

  // Load commissions on component mount
  useEffect(() => {
    fetchCommissions();
  }, []);

  // Fetch project types from API (similar to ProjectType.js fetchProjectTypes)
  const fetchProjectTypes = async () => {
    setLoadingProjectTypes(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=projectType`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const types = Array.isArray(result.data)
        ? result.data
          .filter(item => item.status === true) // Only active project types
          .map((item) => ({
            id: item._id,
            name: item.details?.projectType || item.details?.name || 'Unknown',
            status: item.status === true ? 'Active' : 'Inactive',
            rawStatus: item.status,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
        : [];

      setProjectTypes(types);
    } catch (err) {
      console.error('Failed to load project types:', err);
      setProjectTypes([]);
    } finally {
      setLoadingProjectTypes(false);
    }
  };

  // Load project types on component mount
  useEffect(() => {
    fetchProjectTypes();
  }, []);

  // Helper functions
  const getFilteredInventory = () => {
    // All filters are applied on the server; show data as returned.
    return inventoryData;
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Available':
      case 'available':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32' };
      case 'Under Construction':
        return { backgroundColor: '#fff3e0', color: '#f57c00', border: '1px solid #f57c00' };
      case 'Booked':
      case 'booked':
        return { backgroundColor: '#e3f2fd', color: '#1976d2', border: '1px solid #1976d2' };
      case 'Hold':
      case 'hold':
        return { backgroundColor: '#fff9c4', color: '#f57f17', border: '1px solid #f57f17' };
      case 'Sold':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2', border: '1px solid #7b1fa2' };
      case 'Mortgage':
        return { backgroundColor: '#fff9c4', color: '#f57f17', border: '1px solid #f57f17' };
      case 'Registry':
        return { backgroundColor: '#c8e6c9', color: '#388e3c', border: '1px solid #388e3c' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666', border: '1px solid #666' };
    }
  };

  const getTypeStyle = (type) => {
    switch (type) {
      case 'Plot':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'Apartment':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Villa':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Plot':
        return <SiteIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'Apartment':
        return <ApartmentIcon sx={{ fontSize: 16, color: '#2e7d32' }} />;
      case 'Villa':
        return <VillaIcon sx={{ fontSize: 16, color: '#f57c00' }} />;
      default:
        return null;
    }
  };

  const getTypeValue = (item) => {
    if (item.type === 'Plot' || item.type === 'RowHouse') {
      return item.plotAmount || 0;
    }
    return item.guidelineValue || 0;
  };

  const formatCurrency = (amount) => {
    const numAmount = Number(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const calculateSquareFt = (item) => {
    if (item.type === 'Plot') {
      // Use totalSize if available, otherwise calculate from w1 x l1 or size string
      if (item.totalSize) return item.totalSize;
      if (item.w1 && item.l1) return item.w1 * item.l1;
      if (item.size) {
        const parts = item.size.split('x');
        if (parts.length === 2) {
          const length = parseInt(parts[0].trim());
          const width = parseInt(parts[1].trim());
          if (!isNaN(length) && !isNaN(width)) {
            return length * width;
          }
        }
      }
      return '-';
    }
    if (item.type === 'Apartment') {
      // Use carpetArea or squareFt if available
      if (item.carpetArea) {
        // Extract number from "1200 sq.ft" format
        const match = item.carpetArea.match(/(\d+)/);
        if (match) return parseInt(match[1]);
        return item.carpetArea;
      }
      if (item.squareFt) return item.squareFt;
      // Default values based on configuration
      if (item.configuration === '2 BHK') return 1200;
      if (item.configuration === '3 BHK') return 1800;
      if (item.configuration === '4 BHK') return 2400;
      return '-';
    }
    if (item.type === 'Villa') {
      // Use plotArea or builtUpArea
      if (item.plotArea) {
        const match = item.plotArea.match(/(\d+)/);
        if (match) return parseInt(match[1]);
        return item.plotArea;
      }
      if (item.builtUpArea) {
        const match = item.builtUpArea.match(/(\d+)/);
        if (match) return parseInt(match[1]);
        return item.builtUpArea;
      }
      if (item.squareFt) return item.squareFt;
      // Default values based on configuration
      if (item.configuration === '3 BHK') return 2400;
      if (item.configuration === '4 BHK') return 3000;
      return '-';
    }
    return '-';
  };

  const getOccupancyRate = (available, total) => {
    return ((total - available) / total * 100).toFixed(1);
  };

  const getBookingOrPendingAmount = (item) => {
    // For plots, show booking amount
    if (item.type === 'Plot' && item.bookingAmount) {
      return item.bookingAmount;
    }
    // For apartments and villas, show booking amount if available, otherwise pending amount
    if (item.bookingAmount !== undefined) {
      return item.bookingAmount;
    }
    if (item.pendingAmount !== undefined) {
      return item.pendingAmount;
    }
    // Calculate pending amount as 10% of guideline value if not provided
    if (item.guidelineValue) {
      return Math.round(item.guidelineValue * 0.1);
    }
    return null;
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setViewDialogOpen(true);
  };

  const handleChangePage = (_, newPage) => {
    fetchInventory(newPage, rowsPerPage);
  };
  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    fetchInventory(0, newRows);
  };

  const handleSearch = () => {
    fetchInventory(0, rowsPerPage);
    setSuccessMessage('');
  };

  const handleReset = () => {
    const resetFilters = {
      searchQuery: '',
      searchType: 'all',
      statusFilter: 'all',
      selectedProject: 'all',
      priceFrom: '',
      priceTo: '',
    };

    setSearchQuery(resetFilters.searchQuery);
    setSearchType(resetFilters.searchType);
    setStatusFilter(resetFilters.statusFilter);
    setPriceFrom(resetFilters.priceFrom);
    setPriceTo(resetFilters.priceTo);
    setSelectedProject(resetFilters.selectedProject);
    setSortBy('');
    setTempSortBy('');
    setTempPriceFrom('');
    setTempPriceTo('');
    setPage(0);
    setSuccessMessage('');
    fetchInventory(0, rowsPerPage, {
      ...resetFilters,
      sortBy: '',
    });
  };

  const filteredData = getFilteredInventory();

  const buildExportRows = (list) => {
    return list.map((item) => ({
      'Type': item.type,
      'ID / Unit No': item.identifier,
      'Project Name': item.name,
      'W1 (ft)': item.w1 || '-',
      'L1 (ft)': item.l1 || '-',
      'Sq. Ft.': calculateSquareFt(item),
      'Facing': item.facing || '-',
      'Price/sq.ft': item.pricePerSqFt || '-',
      'PLC%': item.plcPercentage || 0,
      'Amount': item.plotAmount || item.amount,
      'Status': item.status,
      'Remark': item.remark || '-',
      'City': item.city || '-',
      'State': item.state || '-'
    }));
  };

  const handleExportPdf = () => {
    if (!filteredData.length) {
      setFetchError('No inventory data available to export.');
      return;
    }
    const rows = buildExportRows(filteredData);
    exportAsPDF({ rows, title: 'Inventory List', fileNameBase: 'Inventory_Export' });
  };

  const handleExportCsv = () => {
    if (!filteredData.length) {
      setFetchError('No inventory data available to export.');
      return;
    }
    const rows = buildExportRows(filteredData);
    exportAsCSV({ rows, fileNameBase: 'Inventory_Export' });
  };

  const handleExportExcel = () => {
    if (!filteredData.length) {
      setFetchError('No inventory data available to export.');
      return;
    }
    const rows = buildExportRows(filteredData);
    exportAsExcel({ rows, sheetName: 'Inventory', fileNameBase: 'Inventory_Export' });
  };

  // Totals are based on server-filtered inventoryData
  const typeFilteredData = inventoryData;

  // Calculate total inventory metrics; prefer backend totals when available
  const totalInventoryValueBackend = summaryTotals.totalAmount ?? 0;
  const totalUnitsBackend = summaryTotals.count ?? 0;

  const totalInventoryValueFallback = typeFilteredData.reduce((sum, item) => {
    if (item.type === 'Plot') {
      return sum + (item.inventoryValue || 0);
    }
    return sum + (getTypeValue(item) * item.totalUnits);
  }, 0);

  const totalUnitsFallback = typeFilteredData.reduce(
    (sum, item) => sum + item.totalUnits,
    0,
  );

  // Expose totals using backend values when present (including 0);
  // fall back to client-side calculations only if backend didn't send them.
  const totalInventoryValue =
    summaryTotals.totalAmount !== null && summaryTotals.totalAmount !== undefined
      ? totalInventoryValueBackend
      : totalInventoryValueFallback;
  const totalUnits =
    summaryTotals.count !== null && summaryTotals.count !== undefined
      ? totalUnitsBackend
      : totalUnitsFallback;

  const totalAvailableUnits = typeFilteredData.reduce(
    (sum, item) => sum + (item.availableUnits || 0),
    0,
  );
  const totalBookingAmount = typeFilteredData.reduce(
    (sum, item) => sum + (item.bookingAmount || 0),
    0,
  );
  const totalBookedUnits = typeFilteredData.reduce(
    (sum, item) => sum + (item.bookedUnits || 0),
    0,
  );

  // Calculate totals for filtered data
  const filteredTotalInventoryValue = filteredData.reduce((sum, item) => {
    if (item.type === 'Plot') {
      return sum + (item.inventoryValue || 0);
    }
    return sum + (getTypeValue(item) * item.totalUnits);
  }, 0);

  const filteredTotalSquareFt = filteredData.reduce((sum, item) => {
    const sqFt = calculateSquareFt(item);
    return typeof sqFt === 'number' ? sum + sqFt : sum;
  }, 0);

  const filteredTotalW1 = filteredData.reduce((sum, item) => {
    return sum + (item.w1 || 0);
  }, 0);

  const filteredTotalL1 = filteredData.reduce((sum, item) => {
    return sum + (item.l1 || 0);
  }, 0);

  const filteredTotalPrice = filteredData.reduce((sum, item) => {
    return sum + (item.pricePerSqFt || 0);
  }, 0);

  const filteredTotalAmount = filteredData.reduce((sum, item) => {
    return sum + (getTypeValue(item) || 0);
  }, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4" fontWeight="bold" color="primary">
            Inventory Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ExportButton
            onExportPdf={handleExportPdf}
            onExportCsv={handleExportCsv}
            onExportExcel={handleExportExcel}
            variant="outlined"
            size="small"
            sx={{
              height: '40px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchInventory}
            disabled={loadingInventory}
            sx={{ textTransform: 'none', height: '40px' }}
          >
            {loadingInventory ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {fetchError}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Total Inventory Value
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="text.primary">
                    {formatCurrency(totalInventoryValue)}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrendingUpIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Total Units
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="text.primary">
                    {totalUnits.toLocaleString('en-IN')}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SiteIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Available Property
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {summaryTotals.availableCount?.toLocaleString('en-IN') ?? 0}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#e8f5e9',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <EventAvailableIcon sx={{ fontSize: 28, color: '#2e7d32' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 2,
              border: '1px solid #e0e0e0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Booked Property
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {summaryTotals.bookedCount?.toLocaleString('en-IN') ?? 0}
                  </Typography>
                </Box>
                <Box sx={{
                  backgroundColor: '#fff3e0',
                  borderRadius: '50%',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ReportIcon sx={{ fontSize: 28, color: '#f57c00' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              label="Search by ID or Name..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={searchType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setSearchType(newType);
                  setSelectedProject('all');
                }}
                label="Type"
                disabled={loadingProjectTypes}
              >
                <MenuItem value="all">All Types</MenuItem>
                {projectTypes.map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Select Project</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setPage(0);
                }}
                label="Select Project"
                disabled={loadingProjects}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project._id || project.id} value={project._id || project.id}>
                    {project.projectName || project.name || 'Unnamed'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Available">Available </MenuItem>
                <MenuItem value="Under Construction">Under Construction</MenuItem>
                <MenuItem value="Booked">Booked</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="contained"
              onClick={handleSearch}
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                height: '56px'
              }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="outlined"
              onClick={handleReset}
              fullWidth
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                height: '56px'
              }}
            >
              Reset
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <MoreFilters
              expanded={moreFiltersOpen}
              onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
              sortBy={tempSortBy}
              onChangeSortBy={setTempSortBy}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              type="inventory"
            >
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Price From (₹)"
                    type="number"
                    variant="outlined"
                    value={tempPriceFrom}
                    onChange={(e) => setTempPriceFrom(e.target.value)}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Price To (₹)"
                    type="number"
                    variant="outlined"
                    value={tempPriceTo}
                    onChange={(e) => setTempPriceTo(e.target.value)}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </MoreFilters>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>ID / Unit No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Project Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>W1 (ft)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>L1 (ft)</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Sq. Ft.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Facing</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Price/sq.ft</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>PLC%</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Remark</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingInventory ? (
                <TableRow>
                  <TableCell colSpan={15} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                      Loading inventory...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      backgroundColor:
                        item.status?.toLowerCase() == 'booked' ? '#e3f2fc' :
                          item.status?.toLowerCase() == 'available' ? '#e9f5ea' :
                            item.status?.toLowerCase() == 'hold' ? '#f5aeae' :
                              item.status?.toLowerCase() == 'under construction' ? '#cfd0d4' : '#ffffff',
                      '& .MuiTableCell-root': {
                        color: item.status?.toLowerCase() == 'booked' ? 'white' : 'inherit',
                      },
                    }}
                  >
                    <TableCell sx={{
                      color: item.status?.toLowerCase() == 'booked' ? '#000000 !important' : 'inherit'
                    }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getTypeIcon(item.type)}
                        label={item.type}
                        size="small"
                        style={getTypeStyle(item.type)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="medium" color="primary">
                        {item.identifier}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getTypeIcon(item.type)}
                        <Typography variant="body2" sx={{ ml: 0.5, color: item.status?.toLowerCase() === 'booked' ? '#000' : 'inherit' }}>
                          {item.projectName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: item.status?.toLowerCase() === 'booked' ? '#000' : 'inherit' }}>{item.w1 ? `${item.w1} ft` : '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: item.status?.toLowerCase() === 'booked' ? '#000' : 'inherit' }}>{item.l1 ? `${item.l1} ft` : '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="#000">
                        {calculateSquareFt(item)}{' '}
                        {typeof calculateSquareFt(item) === 'number' ? 'sq.ft' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.facing || '-'} size="small" variant="outlined" color="info" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.pricePerSqFt ? `₹${item.pricePerSqFt.toFixed(2)}/sq.ft` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={`${item.plcPercentage || 0}%`} size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          {formatCurrency(item.plotAmount || item.amount)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.status} size="small" style={getStatusStyle(item.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                        {item.remark || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton color="info" size="small" onClick={() => handleView(item)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No inventory found matching your criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* Totals Row */}
              {!loadingInventory && filteredData.length > 0 && (
                <TableRow
                  sx={{
                    backgroundColor: '#fafafa',
                    borderTop: '2px solid #e0e0e0',
                    '& .MuiTableCell-root': {
                      borderBottom: 'none',
                      py: 1.5,
                    }
                  }}
                >
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      Total ({filteredData.length} items)
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {filteredTotalW1.toLocaleString('en-IN')} ft
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {filteredTotalL1.toLocaleString('en-IN')} ft
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {filteredTotalSquareFt.toLocaleString('en-IN')} sq.ft
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      ₹{filteredTotalPrice.toLocaleString('en-IN')}
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {formatCurrency(filteredTotalAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination

          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Inventory Details - {selectedItem?.identifier}
            </Typography>
          </Box>
          <IconButton onClick={() => setViewDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedItem && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip icon={getTypeIcon(selectedItem.type)} label={selectedItem.type} size="small" style={getTypeStyle(selectedItem.type)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedItem.status} size="small" style={getStatusStyle(selectedItem.status)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Project Name</Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Identifier / Unit No</Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.identifier}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Square Feet</Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                  {typeof calculateSquareFt(selectedItem) === 'number'
                    ? `${calculateSquareFt(selectedItem)} sq.ft`
                    : calculateSquareFt(selectedItem)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Sq. Ft.</Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                  {typeof calculateSquareFt(selectedItem) === 'number'
                    ? `${calculateSquareFt(selectedItem)} sq.ft`
                    : calculateSquareFt(selectedItem)}
                </Typography>
              </Grid>

              {selectedItem.type === 'Plot' && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Plot Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Site Name</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.siteName || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Sector</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.sector || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Block</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.block || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Plot Size</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.size || '-'}</Typography>
                  </Grid>
                  {(selectedItem.w1 || selectedItem.l1) && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Width (W1)</Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.w1 ? `${selectedItem.w1} ft` : '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Length (L1)</Typography>
                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.l1 ? `${selectedItem.l1} ft` : '-'}</Typography>
                      </Grid>
                    </>
                  )}
                  {selectedItem.totalSize && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Total Size</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.totalSize} sq.ft</Typography>
                    </Grid>
                  )}
                  {selectedItem.facing && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Facing</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.facing}</Typography>
                    </Grid>
                  )}
                  {selectedItem.price && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Price per sq.ft</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>₹{selectedItem.price}</Typography>
                    </Grid>
                  )}
                  {selectedItem.plcPercentage && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">PLC Percentage</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.plcPercentage}%</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Plot Amount</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                      {formatCurrency(selectedItem.plotAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Booking Amount</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                      {formatCurrency(selectedItem.bookingAmount)}
                    </Typography>
                  </Grid>
                  {selectedItem.pendingAmount !== undefined && selectedItem.pendingAmount !== null && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.pendingAmount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.amount && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.amount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.guidelineValue && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Circle Rate</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.guidelineValue)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.inventoryValue && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Total Inventory Value</Typography>
                      <Typography variant="h6" color="success.main" fontWeight="bold" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.inventoryValue)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.remark && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Remark</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.remark}</Typography>
                    </Grid>
                  )}
                </>
              )}

              {selectedItem.type === 'Apartment' && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Apartment Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Configuration</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.configuration || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Developer</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.developer || '-'}</Typography>
                  </Grid>
                  {selectedItem.category && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Category</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.category}</Typography>
                    </Grid>
                  )}
                  {selectedItem.carpetArea && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Carpet Area</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.carpetArea}</Typography>
                    </Grid>
                  )}
                  {selectedItem.floor && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Floor Range</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.floor}</Typography>
                    </Grid>
                  )}
                  {selectedItem.parking && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Parking</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.parking}</Typography>
                    </Grid>
                  )}
                  {(selectedItem.commission || commissionData.length > 0) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Commission</Typography>
                      <Typography variant="body1" fontWeight="500" color="success.main" sx={{ mt: 0.5 }}>
                        {selectedItem.commission ||
                          (commissionData.length > 0 ? `${Math.max(0, commissionData[0].commissionPercentage - 1)}%` : 'N/A')}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Circle Rate</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                      {formatCurrency(selectedItem.guidelineValue)}
                    </Typography>
                  </Grid>
                  {selectedItem.bookingAmount && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Booking Amount</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.bookingAmount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.pendingAmount !== undefined && selectedItem.pendingAmount !== null && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.pendingAmount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.reraNo && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">RERA Number</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.reraNo}</Typography>
                    </Grid>
                  )}
                  {selectedItem.possessionDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Possession Date</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.possessionDate}</Typography>
                    </Grid>
                  )}
                  {selectedItem.totalUnits && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Total Units</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {selectedItem.totalUnits}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              {selectedItem.type === 'Villa' && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Villa Details</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Configuration</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.configuration || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Developer</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.developer || '-'}</Typography>
                  </Grid>
                  {selectedItem.category && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Category</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.category}</Typography>
                    </Grid>
                  )}
                  {selectedItem.plotArea && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Plot Area</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.plotArea}</Typography>
                    </Grid>
                  )}
                  {selectedItem.builtUpArea && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Built-up Area</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.builtUpArea}</Typography>
                    </Grid>
                  )}
                  {selectedItem.floors && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Floors</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.floors}</Typography>
                    </Grid>
                  )}
                  {selectedItem.parking && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Parking</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.parking}</Typography>
                    </Grid>
                  )}
                  {selectedItem.facing && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Facing</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.facing}</Typography>
                    </Grid>
                  )}
                  {(selectedItem.commission || commissionData.length > 0) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Commission</Typography>
                      <Typography variant="body1" fontWeight="500" color="success.main" sx={{ mt: 0.5 }}>
                        {selectedItem.commission ||
                          (commissionData.length > 0 ? `${Math.max(0, commissionData[0].commissionPercentage - 1)}%` : 'N/A')}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Circle Rate</Typography>
                    <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                      {formatCurrency(selectedItem.guidelineValue)}
                    </Typography>
                  </Grid>
                  {selectedItem.bookingAmount && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Booking Amount</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.bookingAmount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.pendingAmount !== undefined && selectedItem.pendingAmount !== null && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Pending Amount</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {formatCurrency(selectedItem.pendingAmount)}
                      </Typography>
                    </Grid>
                  )}
                  {selectedItem.reraNo && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">RERA Number</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.reraNo}</Typography>
                    </Grid>
                  )}
                  {selectedItem.possessionDate && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Possession Date</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.possessionDate}</Typography>
                    </Grid>
                  )}
                  {selectedItem.totalUnits && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Total Units</Typography>
                      <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                        {selectedItem.totalUnits}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Location Details</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">City</Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                  {selectedItem.city || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">State</Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>{selectedItem.state || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body1" fontWeight="500">{selectedItem.address || '-'}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory;
